import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ExternalLink, Heart, Check, Shield, ThumbsUp, ThumbsDown, Briefcase, Users, Zap, Target, Star, Send } from 'lucide-react';
import { Header } from '../components/Header';
import { GlassCard } from '../components/ui/GlassCard';
import { DonateModal } from '../components/DonateModal';
import { useTools } from '../hooks/useTools';
import { useFavoritesContext } from '../contexts/FavoritesContext';
import { useToolVotes } from '../hooks/useToolVotes';
import { useToolReviews } from '../hooks/useToolReviews';
import { useAuth } from '../hooks/useAuth';
// Convert tool name to URL-safe slug
export function createToolSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
// Modern minimal loading component
function LoadingState() {
  return <div className="min-h-screen bg-black text-white flex items-center justify-center" data-id="element-1213">
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-1214">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full blur-[120px] animate-pulse" data-id="element-1215" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-purple-500/10 to-pink-500/10 rounded-full blur-[120px] animate-pulse" data-id="element-1216" />
      </div>
      <div className="relative z-10 flex flex-col items-center gap-4" data-id="element-1217">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'linear'
      }} className="w-8 h-8 rounded-full border-2 border-cyan-500/20 border-t-cyan-500" data-id="element-1218" />
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.2
      }} className="text-sm text-gray-400" style={{
        fontFamily: 'Space Grotesk, sans-serif'
      }} data-id="element-1219">
          Fetching the latest information...
        </motion.p>
      </div>
    </div>;
}
// Generate expanded description
function generateExpandedDescription(tool: any): string {
  const baseDescription = tool.description;
  const primaryCategory = (tool.categories && tool.categories.length > 0 ? tool.categories[0] : tool.category).toLowerCase();
  const categoryContext: Record<string, string> = {
    image: "This powerful image generation tool leverages advanced AI models to transform text descriptions into stunning visual content. Whether you're a designer, marketer, or creative professional, this tool streamlines your workflow and unlocks new creative possibilities.",
    video: 'This cutting-edge video generation platform uses artificial intelligence to create, edit, and enhance video content. From automated editing to AI-generated scenes, it empowers creators to produce professional-quality videos with unprecedented speed and ease.',
    audio: 'This innovative audio AI tool revolutionizes sound creation and manipulation. Whether you need voice synthesis, music generation, or audio enhancement, it provides professional-grade results that were previously only achievable with expensive studio equipment.',
    development: 'This AI-powered development tool accelerates coding workflows and improves code quality. By understanding context and providing intelligent suggestions, it helps developers write better code faster, reduce bugs, and learn new technologies more efficiently.',
    writing: "This advanced writing assistant uses natural language processing to help create, edit, and refine written content. From blog posts to technical documentation, it enhances writing quality, maintains consistent tone, and helps overcome writer's block.",
    design: 'This AI design tool transforms the creative process by automating repetitive tasks and providing intelligent design suggestions. It enables designers to iterate faster, explore more concepts, and deliver polished results with greater efficiency.',
    productivity: 'This productivity-focused AI tool streamlines workflows and automates routine tasks. By intelligently organizing information and providing smart suggestions, it helps individuals and teams work more efficiently and focus on high-value activities.',
    research: 'This research-oriented AI platform accelerates information discovery and analysis. It helps researchers, students, and professionals quickly find relevant information, synthesize insights, and stay current with the latest developments in their field.'
  };
  const context = categoryContext[primaryCategory] || 'This AI-powered tool provides innovative solutions for modern workflows. By leveraging cutting-edge machine learning technology, it helps users achieve better results with less effort and time investment.';
  return `${baseDescription}\n\n${context}\n\nThe platform combines intuitive design with powerful capabilities, making advanced AI technology accessible to users of all skill levels. Regular updates ensure access to the latest features and improvements, while a growing community provides support and shares best practices.`;
}
// Generate use cases
function generateUseCases(categories: string[]): Array<{
  icon: any;
  title: string;
  description: string;
}> {
  const primaryCategory = categories[0] || 'Other';
  const useCasesByCategory: Record<string, Array<{
    icon: any;
    title: string;
    description: string;
  }>> = {
    Image: [{
      icon: Briefcase,
      title: 'Marketing & Advertising',
      description: 'Create eye-catching visuals for social media campaigns, ad creatives, and promotional materials without hiring a designer.'
    }, {
      icon: Users,
      title: 'Content Creation',
      description: 'Generate unique illustrations, thumbnails, and graphics for blogs, videos, and digital content at scale.'
    }, {
      icon: Zap,
      title: 'Product Design',
      description: 'Rapidly prototype product concepts, packaging designs, and mockups to visualize ideas before production.'
    }, {
      icon: Target,
      title: 'Brand Identity',
      description: 'Develop logo concepts, brand assets, and visual identity elements to establish a cohesive brand presence.'
    }],
    Video: [{
      icon: Briefcase,
      title: 'Marketing Videos',
      description: 'Produce engaging promotional videos, product demos, and explainer content for social media and websites.'
    }, {
      icon: Users,
      title: 'Educational Content',
      description: 'Create training materials, tutorials, and course content with automated editing and professional polish.'
    }, {
      icon: Zap,
      title: 'Social Media',
      description: 'Generate short-form video content optimized for TikTok, Instagram Reels, and YouTube Shorts.'
    }, {
      icon: Target,
      title: 'Corporate Communications',
      description: 'Produce internal announcements, presentations, and company updates with consistent branding.'
    }],
    Audio: [{
      icon: Briefcase,
      title: 'Podcast Production',
      description: 'Enhance audio quality, remove background noise, and generate intro/outro music for professional podcasts.'
    }, {
      icon: Users,
      title: 'Voice-Overs',
      description: 'Create natural-sounding narration for videos, presentations, and e-learning content in multiple languages.'
    }, {
      icon: Zap,
      title: 'Music Creation',
      description: 'Generate background music, jingles, and soundtracks for videos, games, and multimedia projects.'
    }, {
      icon: Target,
      title: 'Accessibility',
      description: 'Convert written content to audio for visually impaired users and create audio descriptions for media.'
    }],
    Development: [{
      icon: Briefcase,
      title: 'Code Generation',
      description: 'Accelerate development by generating boilerplate code, functions, and components from natural language descriptions.'
    }, {
      icon: Users,
      title: 'Code Review',
      description: 'Identify bugs, security vulnerabilities, and code quality issues with AI-powered analysis and suggestions.'
    }, {
      icon: Zap,
      title: 'Documentation',
      description: 'Automatically generate code documentation, API references, and technical guides from your codebase.'
    }, {
      icon: Target,
      title: 'Learning & Debugging',
      description: 'Get explanations of complex code, debug errors faster, and learn new programming concepts interactively.'
    }],
    Writing: [{
      icon: Briefcase,
      title: 'Content Marketing',
      description: 'Create blog posts, articles, and SEO-optimized content that drives traffic and engages audiences.'
    }, {
      icon: Users,
      title: 'Copywriting',
      description: 'Generate compelling ad copy, product descriptions, and sales pages that convert visitors to customers.'
    }, {
      icon: Zap,
      title: 'Email Campaigns',
      description: 'Draft personalized email sequences, newsletters, and outreach messages that get responses.'
    }, {
      icon: Target,
      title: 'Creative Writing',
      description: "Brainstorm story ideas, develop characters, and overcome writer's block for novels and scripts."
    }],
    Design: [{
      icon: Briefcase,
      title: 'UI/UX Design',
      description: 'Generate wireframes, mockups, and user interface designs with AI-powered layout suggestions.'
    }, {
      icon: Users,
      title: 'Brand Assets',
      description: 'Create consistent brand materials including logos, color palettes, and typography systems.'
    }, {
      icon: Zap,
      title: 'Rapid Prototyping',
      description: 'Quickly iterate on design concepts and create interactive prototypes for user testing.'
    }, {
      icon: Target,
      title: 'Design Systems',
      description: 'Build and maintain scalable design systems with reusable components and style guides.'
    }],
    Productivity: [{
      icon: Briefcase,
      title: 'Task Automation',
      description: 'Automate repetitive workflows, data entry, and routine tasks to save hours each week.'
    }, {
      icon: Users,
      title: 'Meeting Management',
      description: 'Transcribe meetings, generate summaries, and extract action items automatically.'
    }, {
      icon: Zap,
      title: 'Email Management',
      description: 'Organize inbox, draft responses, and prioritize important messages with AI assistance.'
    }, {
      icon: Target,
      title: 'Project Planning',
      description: 'Break down complex projects, estimate timelines, and track progress with intelligent insights.'
    }],
    Research: [{
      icon: Briefcase,
      title: 'Literature Review',
      description: 'Quickly scan and summarize academic papers, research articles, and scientific publications.'
    }, {
      icon: Users,
      title: 'Data Analysis',
      description: 'Analyze datasets, identify patterns, and generate insights from complex research data.'
    }, {
      icon: Zap,
      title: 'Competitive Intelligence',
      description: 'Monitor competitors, track industry trends, and gather market intelligence efficiently.'
    }, {
      icon: Target,
      title: 'Knowledge Synthesis',
      description: 'Connect ideas across multiple sources and generate comprehensive research summaries.'
    }]
  };
  return useCasesByCategory[primaryCategory] || [{
    icon: Briefcase,
    title: 'Professional Use',
    description: 'Streamline professional workflows and deliver high-quality results faster.'
  }, {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Enable seamless collaboration and knowledge sharing across teams.'
  }, {
    icon: Zap,
    title: 'Rapid Execution',
    description: 'Execute tasks quickly with AI-powered automation and intelligent assistance.'
  }, {
    icon: Target,
    title: 'Goal Achievement',
    description: 'Achieve your objectives more efficiently with smart tools and insights.'
  }];
}
// Get category color
const getCategoryColor = (category: string) => {
  const colorMap: Record<string, string> = {
    Image: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    Video: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
    Audio: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    Development: 'bg-green-500/10 text-green-400 border-green-500/20',
    Writing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    Marketing: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    Design: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    Productivity: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    Research: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
  };
  return colorMap[category] || 'bg-white/5 text-gray-400 border-white/10';
};
export function ToolDetailPage() {
  const {
    toolName
  } = useParams();
  const {
    tools,
    loading
  } = useTools();
  const {
    toggleFavorite,
    isFavorite
  } = useFavoritesContext();
  const {
    user,
    signInWithGoogle
  } = useAuth();
  const [logoError, setLogoError] = useState(false);
  const [fallbackIndex, setFallbackIndex] = useState(0);
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  // Find the tool by name slug from URL
  const tool = tools.find(t => createToolSlug(t.name) === toolName);
  // Voting hook
  const {
    votes,
    loading: votesLoading,
    vote
  } = useToolVotes(tool?.id || '');
  const [isVoting, setIsVoting] = useState(false);
  // Reviews hook
  const {
    reviews,
    userReview,
    loading: reviewsLoading,
    submitReview
  } = useToolReviews(tool?.id || '');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [ratingEaseOfUse, setRatingEaseOfUse] = useState(5);
  const [ratingPricing, setRatingPricing] = useState(5);
  const [ratingUtility, setRatingUtility] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  // Logo fallback handling
  const domain = tool?.website_url ? new URL(tool.website_url).hostname : '';
  const logoSources = tool ? [tool.logo_url, `https://logo.clearbit.com/${domain}`, `https://www.google.com/s2/favicons?domain=${domain}&sz=128`, `https://icons.duckduckgo.com/ip3/${domain}.ico`].filter(Boolean) : [];
  const currentLogoUrl = logoSources[fallbackIndex];
  const handleLogoError = () => {
    if (fallbackIndex < logoSources.length - 1) {
      setFallbackIndex(fallbackIndex + 1);
    } else {
      setLogoError(true);
    }
  };
  // Get category emoji
  const getCategoryEmoji = (category: string) => {
    const emojiMap: Record<string, string> = {
      Image: '🎨',
      Video: '🎬',
      Audio: '🎵',
      Development: '💻',
      Writing: '✍️',
      Design: '🎨',
      Productivity: '⚡',
      Research: '🔬',
      Marketing: '📱',
      Other: '🔧'
    };
    return emojiMap[category] || '🔧';
  };
  // Get pricing type
  const getPricingType = (tiers: any[]) => {
    if (!tiers || tiers.length === 0) return 'Contact';
    const hasFree = tiers.some(t => t.price.toLowerCase().includes('free'));
    const hasPaid = tiers.some(t => !t.price.toLowerCase().includes('free'));
    if (hasFree && hasPaid) return 'Freemium';
    if (hasFree) return 'Free';
    return 'Paid';
  };
  // Handle voting
  const handleVote = async (voteType: 'up' | 'down') => {
    if (!user) {
      signInWithGoogle();
      return;
    }
    try {
      setIsVoting(true);
      await vote(voteType);
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setIsVoting(false);
    }
  };
  // Handle review submission
  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      signInWithGoogle();
      return;
    }
    if (!reviewText.trim()) {
      return;
    }
    try {
      setIsSubmittingReview(true);
      await submitReview(ratingEaseOfUse, ratingPricing, ratingUtility, reviewText.trim());
      setReviewText('');
      setRatingEaseOfUse(5);
      setRatingPricing(5);
      setRatingUtility(5);
      setShowReviewForm(false);
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setIsSubmittingReview(false);
    }
  };
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Calculate average rating
  const getAverageRating = (review: ToolReview) => {
    return ((review.rating_ease_of_use + review.rating_pricing + review.rating_utility) / 3).toFixed(1);
  };
  // Loading state
  if (loading) {
    return <LoadingState data-id="element-1220" />;
  }
  // Not found state
  if (!tool) {
    return <div className="min-h-screen bg-black text-white" data-id="element-1221">
        <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-1222">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full blur-[120px]" data-id="element-1223" />
          <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-purple-500/10 to-pink-500/10 rounded-full blur-[120px]" data-id="element-1224" />
        </div>
        <Header data-id="element-1225" />
        <div className="relative z-10 pt-16" data-id="element-1226">
          <main className="max-w-6xl mx-auto px-4 py-12" data-id="element-1227">
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 group" data-id="element-1228">
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" data-id="element-1229" />
              <span data-id="element-1230">Back to Home</span>
            </Link>
            <GlassCard className="p-12 text-center" data-id="element-1231">
              <h1 className="text-3xl font-bold text-white mb-4" data-id="element-1232">
                Tool Not Found
              </h1>
              <p className="text-gray-400 mb-6" data-id="element-1233">
                The tool you're looking for doesn't exist or has been removed.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-all" data-id="element-1234">
                Browse All Tools
              </Link>
            </GlassCard>
          </main>
        </div>
      </div>;
  }
  const isAdmin = (tool as any).source === 'admin';
  const pricingType = getPricingType(tool.pricing_tiers || []);
  const displayCategories = tool.categories && tool.categories.length > 0 ? tool.categories : [tool.category];
  const expandedDescription = generateExpandedDescription(tool);
  const useCases = generateUseCases(displayCategories);
  return <div className="min-h-screen bg-black text-white" data-id="element-1235">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-1236">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full blur-[120px]" data-id="element-1237" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-purple-500/10 to-pink-500/10 rounded-full blur-[120px]" data-id="element-1238" />
      </div>

      <Header data-id="element-1239" />

      <div className="relative z-10 pt-16" data-id="element-1240">
        <main className="max-w-6xl mx-auto px-4 py-12 pt-[72px]" data-id="element-1241">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 group" data-id="element-1242">
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" data-id="element-1243" />
            <span data-id="element-1244">Back to Home</span>
          </Link>

          {/* Hero Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1245">
            <GlassCard className="p-8 mb-8" data-id="element-1246">
              <div className="flex flex-col md:flex-row gap-6" data-id="element-1247">
                {/* Logo - 50% smaller on mobile */}
                <div className="shrink-0" data-id="element-1248">
                  <div className="w-[55px] h-[55px] md:w-[109px] md:h-[109px] flex items-center justify-center overflow-hidden" data-id="element-1249">
                    {!logoError && currentLogoUrl ? <img src={currentLogoUrl} alt={`${tool.name} logo`} className="w-full h-full object-contain rounded-[18px] md:rounded-[35px]" onError={handleLogoError} data-id="element-1250" /> : <div className="text-2xl md:text-5xl" data-id="element-1251">
                        {getCategoryEmoji(displayCategories[0])}
                      </div>}
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" data-id="element-1252">
                  {/* Title and Favorite Button */}
                  <div className="flex items-start justify-between gap-4 mb-4" data-id="element-1253">
                    <div className="flex-1 min-w-0" data-id="element-1254">
                      <h1 className="text-2xl md:text-[2rem] font-bold text-white mb-2 font-special leading-tight [line-height:2.4rem] md:[line-height:4.2rem]" data-id="element-1255">
                        {tool.name}
                      </h1>
                      <p className="text-gray-400 text-base md:text-lg" data-id="element-1256">
                        {tool.description}
                      </p>
                    </div>
                    <button onClick={() => toggleFavorite(tool.id)} className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all shrink-0" data-id="element-1257">
                      <Heart size={20} className={`transition-colors ${isFavorite(tool.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} data-id="element-1258" />
                    </button>
                  </div>

                  {/* Mobile: Categories on right, Desktop: Categories and button in row */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1259">
                    {/* Category Tags */}
                    <div className="flex flex-wrap items-center gap-2" data-id="element-1260">
                      {displayCategories.map((category, index) => <span key={index} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${getCategoryColor(category)}`} data-id="element-1261">
                          {category}
                        </span>)}
                      {isAdmin && <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs font-medium" data-id="element-1262">
                          <Shield size={12} data-id="element-1263" />
                          Curated
                        </span>}
                      <span className="px-2.5 py-1 rounded-lg bg-white/5 text-gray-400 border border-white/10 text-xs font-medium" data-id="element-1264">
                        {pricingType}
                      </span>
                    </div>

                    {/* Website Button - left aligned on mobile, right on desktop */}
                    <a href={tool.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 hover:from-cyan-500 hover:to-purple-500 text-white font-bold transition-all text-sm md:shrink-0" style={{
                    fontFamily: 'Space Grotesk, sans-serif'
                  }} data-id="element-1265">
                      <span data-id="element-1266">Visit Website</span>
                      <ExternalLink size={16} data-id="element-1267" />
                    </a>
                  </div>
                </div>
              </div>
            </GlassCard>
          </motion.div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1268">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8" data-id="element-1269">
              {/* About */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} data-id="element-1270">
                <GlassCard className="p-6" data-id="element-1271">
                  <h2 style={{
                  marginBottom: '20px'
                }} className="text-2xl font-bold text-white font-special" data-id="element-1272">
                    About
                  </h2>
                  <div className="text-gray-400 leading-relaxed space-y-4" data-id="element-1273">
                    {expandedDescription.split('\n\n').map((paragraph, index) => <p key={index} data-id="element-1274">{paragraph}</p>)}
                  </div>
                </GlassCard>
              </motion.div>

              {/* Use Cases */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} data-id="element-1275">
                <GlassCard className="p-6" data-id="element-1276">
                  <h2 style={{
                  marginBottom: '20px'
                }} className="text-2xl font-bold text-white font-special" data-id="element-1277">
                    Use Cases
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1278">
                    {useCases.map((useCase, index) => <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-white/5" data-id="element-1279">
                        {/* Icon with gradient background */}
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 flex items-center justify-center" data-id="element-1280">
                          <useCase.icon size={20} className="text-white" data-id="element-1281" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0" data-id="element-1282">
                          <h3 className="text-white font-bold text-base mb-1" style={{
                        fontFamily: 'Space Grotesk, sans-serif'
                      }} data-id="element-1283">
                            {useCase.title}
                          </h3>
                          <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1284">
                            {useCase.description}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </GlassCard>
              </motion.div>

              {/* Community Feedback */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} data-id="element-1285">
                <GlassCard className="p-6" data-id="element-1286">
                  <h2 style={{
                  marginBottom: '20px'
                }} className="text-2xl font-bold text-white font-special" data-id="element-1287">
                    Give us your Feedback
                  </h2>

                  {!user ? <div className="text-center py-8" data-id="element-1288">
                      <p className="text-gray-400 mb-4" data-id="element-1289">
                        Sign in to vote and share your experience with this tool
                      </p>
                      <button onClick={signInWithGoogle} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-all border border-white/10" data-id="element-1290">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" data-id="element-1291">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-1292" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-1293" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-1294" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-1295" />
                        </svg>
                        Sign in with Google
                      </button>
                    </div> : <div className="space-y-6" data-id="element-1296">
                      {/* Voting Buttons - Icons only, no text labels */}
                      <div className="flex items-center gap-4" data-id="element-1297">
                        <motion.button onClick={() => handleVote('up')} disabled={isVoting || votesLoading} whileHover={{
                      scale: 1.02
                    }} whileTap={{
                      scale: 0.98
                    }} className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border transition-all ${votes.userVote === 'up' ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-green-500/10 hover:border-green-500/30 hover:text-green-400'} ${isVoting || votesLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-1298">
                          <ThumbsUp size={24} className={votes.userVote === 'up' ? 'fill-current' : ''} data-id="element-1299" />
                          <span className="text-2xl font-bold" data-id="element-1300">
                            {votes.upvotes}
                          </span>
                        </motion.button>

                        <motion.button onClick={() => handleVote('down')} disabled={isVoting || votesLoading} whileHover={{
                      scale: 1.02
                    }} whileTap={{
                      scale: 0.98
                    }} className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border transition-all ${votes.userVote === 'down' ? 'bg-red-500/20 border-red-500 text-red-400' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400'} ${isVoting || votesLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-1301">
                          <ThumbsDown size={24} className={votes.userVote === 'down' ? 'fill-current' : ''} data-id="element-1302" />
                          <span className="text-2xl font-bold" data-id="element-1303">
                            {votes.downvotes}
                          </span>
                        </motion.button>
                      </div>

                      {/* Vote Summary */}
                      {(votes.upvotes > 0 || votes.downvotes > 0) && <div className="grid grid-cols-2 gap-4" data-id="element-1304">
                          <div className="text-center p-3 rounded-lg bg-white/5" data-id="element-1305">
                            <div className="text-xl font-bold text-white mb-1" data-id="element-1306">
                              {Math.round(votes.upvotes / (votes.upvotes + votes.downvotes) * 100)}
                              %
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-1307">
                              Positive
                            </div>
                          </div>
                          <div className="text-center p-3 rounded-lg bg-white/5" data-id="element-1308">
                            <div className="text-xl font-bold text-white mb-1" data-id="element-1309">
                              {votes.upvotes + votes.downvotes}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-1310">
                              Total Votes
                            </div>
                          </div>
                        </div>}

                      {/* Review Form - Always shown if no review submitted */}
                      {userReview ? <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/20" data-id="element-1311">
                          <div className="flex items-center gap-2 mb-2" data-id="element-1312">
                            <Check size={16} className="text-cyan-400" data-id="element-1313" />
                            <span className="text-sm font-bold text-cyan-400" data-id="element-1314">
                              {userReview.status === 'pending' ? 'Review Submitted' : 'Review Published'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400" data-id="element-1315">
                            {userReview.status === 'pending' ? 'Your review is pending approval and will be published soon.' : 'Thank you for sharing your experience!'}
                          </p>
                        </div> : <motion.form initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} onSubmit={handleSubmitReview} className="space-y-5 p-5 rounded-lg bg-white/5 border border-white/10" data-id="element-1316">
                          {/* Star Ratings - 3 Categories in 3 columns - Smaller stars on mobile */}
                          <div className="grid grid-cols-3 gap-2 sm:gap-4" data-id="element-1317">
                            {/* Ease of Use */}
                            <div data-id="element-1318">
                              <label className="block text-xs font-medium text-gray-400 mb-2 text-center" data-id="element-1319">
                                Ease of Use
                              </label>
                              <div className="flex gap-0.5 sm:gap-1 justify-center" data-id="element-1320">
                                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setRatingEaseOfUse(star)} className="transition-all hover:scale-110 focus:outline-none" data-id="element-1321">
                                    <svg width="14" height="14" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill={star <= ratingEaseOfUse ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" style={{
                              color: star <= ratingEaseOfUse ? '#FBBF24' : '#4B5563'
                            }} data-id="element-1322">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-1323" />
                                    </svg>
                                  </button>)}
                              </div>
                            </div>

                            {/* Pricing */}
                            <div data-id="element-1324">
                              <label className="block text-xs font-medium text-gray-400 mb-2 text-center" data-id="element-1325">
                                Pricing
                              </label>
                              <div className="flex gap-0.5 sm:gap-1 justify-center" data-id="element-1326">
                                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setRatingPricing(star)} className="transition-all hover:scale-110 focus:outline-none" data-id="element-1327">
                                    <svg width="14" height="14" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill={star <= ratingPricing ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" style={{
                              color: star <= ratingPricing ? '#FBBF24' : '#4B5563'
                            }} data-id="element-1328">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-1329" />
                                    </svg>
                                  </button>)}
                              </div>
                            </div>

                            {/* Utility */}
                            <div data-id="element-1330">
                              <label className="block text-xs font-medium text-gray-400 mb-2 text-center" data-id="element-1331">
                                Utility
                              </label>
                              <div className="flex gap-0.5 sm:gap-1 justify-center" data-id="element-1332">
                                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setRatingUtility(star)} className="transition-all hover:scale-110 focus:outline-none" data-id="element-1333">
                                    <svg width="14" height="14" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill={star <= ratingUtility ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" style={{
                              color: star <= ratingUtility ? '#FBBF24' : '#4B5563'
                            }} data-id="element-1334">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-1335" />
                                    </svg>
                                  </button>)}
                              </div>
                            </div>
                          </div>

                          {/* Review Text */}
                          <div data-id="element-1336">
                            <label className="block text-sm font-medium text-gray-400 mb-2" data-id="element-1337">
                              Your Review
                            </label>
                            <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Share your experience with this tool..." rows={4} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors resize-none" required data-id="element-1338" />
                          </div>

                          {/* Submit Button */}
                          <button type="submit" disabled={isSubmittingReview || !reviewText.trim()} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 hover:from-cyan-500 hover:to-purple-500 text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1339">
                            <Send size={16} data-id="element-1340" />
                            {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
                          </button>
                        </motion.form>}
                    </div>}
                </GlassCard>
              </motion.div>

              {/* User Reviews - Separate Module */}
              {reviews.length > 0 && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} data-id="element-1341">
                  <GlassCard className="p-6" data-id="element-1342">
                    <h2 style={{
                  marginBottom: '20px'
                }} className="text-2xl font-bold text-white font-special" data-id="element-1343">
                      User Reviews ({reviews.length})
                    </h2>
                    <div className="space-y-4" data-id="element-1344">
                      {reviews.map(review => <div key={review.id} className="p-5 rounded-lg bg-white/5 border border-white/10" data-id="element-1345">
                          <div className="flex items-start gap-4 mb-4" data-id="element-1346">
                            {/* User Avatar */}
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-purple-400 flex items-center justify-center text-white font-bold text-sm shrink-0" data-id="element-1347">
                              {review.user_email.charAt(0).toUpperCase()}
                            </div>

                            <div className="flex-1" data-id="element-1348">
                              <div className="flex items-center justify-between mb-2" data-id="element-1349">
                                <div data-id="element-1350">
                                  <div className="flex items-center gap-2 mb-1" data-id="element-1351">
                                    <span className="text-sm font-bold text-white" data-id="element-1352">
                                      {review.user_email.split('@')[0]}
                                    </span>
                                    <div className="flex gap-0.5" data-id="element-1353">
                                      {[1, 2, 3, 4, 5].map(star => <Star key={star} size={14} className={star <= Math.round(parseFloat(getAverageRating(review))) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'} data-id="element-1354" />)}
                                    </div>
                                    <span className="text-sm font-bold text-white" data-id="element-1355">
                                      {getAverageRating(review)}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500" data-id="element-1356">
                                    {formatDate(review.created_at)}
                                  </p>
                                </div>
                              </div>

                              {/* Individual Ratings */}
                              <div className="grid grid-cols-3 gap-2 mb-3" data-id="element-1357">
                                <div className="text-center p-2 rounded bg-white/5" data-id="element-1358">
                                  <div className="text-xs text-gray-500 mb-1" data-id="element-1359">
                                    Ease of Use
                                  </div>
                                  <div className="text-sm font-bold text-white" data-id="element-1360">
                                    {review.rating_ease_of_use}/5
                                  </div>
                                </div>
                                <div className="text-center p-2 rounded bg-white/5" data-id="element-1361">
                                  <div className="text-xs text-gray-500 mb-1" data-id="element-1362">
                                    Pricing
                                  </div>
                                  <div className="text-sm font-bold text-white" data-id="element-1363">
                                    {review.rating_pricing}/5
                                  </div>
                                </div>
                                <div className="text-center p-2 rounded bg-white/5" data-id="element-1364">
                                  <div className="text-xs text-gray-500 mb-1" data-id="element-1365">
                                    Utility
                                  </div>
                                  <div className="text-sm font-bold text-white" data-id="element-1366">
                                    {review.rating_utility}/5
                                  </div>
                                </div>
                              </div>

                              {/* Review Text */}
                              <p className="text-sm text-gray-300 leading-relaxed" data-id="element-1367">
                                {review.review_text}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </GlassCard>
                </motion.div>}
            </div>

            {/* Sidebar - Pricing */}
            <div className="lg:col-span-1" data-id="element-1368">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="sticky top-24" data-id="element-1369">
                <GlassCard className="p-6" data-id="element-1370">
                  <h2 style={{
                  marginBottom: '20px'
                }} className="text-2xl font-bold text-white font-special" data-id="element-1371">
                    Pricing
                  </h2>
                  {tool.pricing_tiers && tool.pricing_tiers.length > 0 ? <div className="space-y-4" data-id="element-1372">
                      {tool.pricing_tiers.map((tier, index) => <div key={index} className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-cyan-500/30 transition-all" data-id="element-1373">
                          <div className="flex items-baseline justify-between mb-3" data-id="element-1374">
                            <h3 className="text-lg font-bold text-white" style={{
                        fontFamily: 'Space Grotesk, sans-serif'
                      }} data-id="element-1375">
                              {tier.name}
                            </h3>
                            <span className="text-lg font-bold text-cyan-400" style={{
                        fontFamily: 'Space Grotesk, sans-serif'
                      }} data-id="element-1376">
                              {tier.price}
                            </span>
                          </div>
                          {tier.features && tier.features.length > 0 && <div className="space-y-2" data-id="element-1377">
                              {tier.features.map((feature, fIndex) => <div key={fIndex} className="flex items-start gap-2" data-id="element-1378">
                                  <Check size={14} className="text-green-400 mt-1 shrink-0" data-id="element-1379" />
                                  <span className="text-sm text-gray-400" data-id="element-1380">
                                    {feature}
                                  </span>
                                </div>)}
                            </div>}
                        </div>)}
                    </div> : <div className="text-center py-8 text-gray-400" data-id="element-1381">
                      <p data-id="element-1382">Pricing information not available.</p>
                      <a href={tool.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 text-sm mt-2 inline-block" data-id="element-1383">
                        Visit website for details
                      </a>
                    </div>}
                </GlassCard>
              </motion.div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 py-6 bg-black/80 backdrop-blur-lg mt-16" data-id="element-1384">
          <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-1385">
            <div className="flex items-center gap-2" data-id="element-1386">
              <span style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontWeight: 300,
              fontSize: '1.125rem',
              lineHeight: '1.75rem',
              letterSpacing: '.15em'
            }} className="text-white" data-id="element-1387">
                FAIND
              </span>
            </div>
            <div className="text-gray-500 text-sm" data-id="element-1388">
              © {currentYear} Faind. All rights reserved.
            </div>
            <div className="flex items-center gap-6" data-id="element-1389">
              <a href="https://x.com/faindyourapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Follow us on X" data-id="element-1390">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" data-id="element-1391">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-1392" />
                </svg>
              </a>
              <button onClick={() => setIsDonateModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-pink-500/30 text-gray-400 hover:text-pink-400 transition-all text-sm font-medium" data-id="element-1393">
                <Heart size={14} data-id="element-1394" />
                Donate
              </button>
            </div>
          </div>
        </footer>

        <DonateModal isOpen={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} data-id="element-1395" />
      </div>
    </div>;
}